import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-800">
            {/* Header - Reused from LandingPage/ContactUs styles */}
            <motion.header
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-white/95 backdrop-blur-md shadow-lg"
                    }`}
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="text-3xl font-bold text-ayurmax-primary"
                        >
                            <Link to="/" className="flex items-center">
                                <motion.img
                                    whileHover={{ scale: 1.05 }}
                                    src="/Ayurmaxfinal.png"
                                    alt="Ayumax"
                                    className="h-16 md:h-20"
                                />
                            </Link>
                        </motion.div>
                        <nav className="hidden md:flex items-center space-x-8">
                            <Link to="/" className="hover:text-ayurmax-primary transition-colors text-gray-700">Home</Link>
                            <Link to="/contact-us" className="hover:text-ayurmax-primary transition-colors text-gray-700">Contact</Link>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate("/login")}
                                className="px-6 py-2 rounded-full font-semibold transition-all bg-ayurmax-primary text-white hover:bg-ayurmax-dark shadow-lg"
                            >
                                Login
                            </motion.button>
                        </nav>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate("/login")}
                            className="md:hidden px-4 py-2 bg-ayurmax-primary text-white rounded-full font-semibold text-sm shadow-lg"
                        >
                            Login
                        </motion.button>
                    </div>
                </div>
            </motion.header>

            {/* Main Content */}
            <main className="flex-1 pt-32 pb-16 px-4 sm:px-6 lg:px-8 container mx-auto max-w-4xl">
                <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-gray-100">
                    <h1 className="text-3xl md:text-5xl font-bold text-ayurmax-primary mb-6">Privacy Policy</h1>
                    <p className="text-gray-500 mb-8 italic">Last Updated: March 2026</p>

                    <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
                        <p>
                            AseetSoft, a product of PIT Labs, provides the Ayurmax software platform for Ayurvedic practitioners.
                            This Privacy Policy describes how data is collected, processed, and protected in accordance with
                            applicable Indian laws including the Digital Personal Data Protection Act (DPDP) 2023.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Data Collected</h3>
                        <p>We may collect:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Doctor and clinic information</li>
                            <li>Contact details</li>
                            <li>Login credentials</li>
                            <li>Patient data entered by doctors</li>
                            <li>Usage and technical logs</li>
                        </ul>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. Role of PIT Labs</h3>
                        <p>
                            PIT Labs acts as a technology service provider and data processor.
                            Doctors and clinics remain Data Controllers for patient information.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Patient Data Responsibility</h3>
                        <p>Users are responsible for:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Obtaining patient consent</li>
                            <li>Maintaining confidentiality</li>
                            <li>Legal compliance</li>
                        </ul>
                        <p className="mt-4">
                            PIT Labs does not access patient data except for technical support.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Data Security</h3>
                        <p>Security measures include:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Encrypted HTTPS access</li>
                            <li>Secure servers</li>
                            <li>Access control</li>
                            <li>Password protection</li>
                        </ul>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. Data Retention</h3>
                        <p>
                            Data is retained while accounts remain active unless deletion is requested.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. Data Sharing</h3>
                        <p>Data is shared only:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>When legally required</li>
                            <li>To protect platform security</li>
                        </ul>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">7. User Rights</h3>
                        <p>Users may request:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Data correction</li>
                            <li>Data deletion</li>
                            <li>Account closure</li>
                        </ul>
                        <p className="mt-4">
                            Requests may be sent to <a href="mailto:reachus@pitlabs.in" className="text-ayurmax-primary hover:underline font-medium">reachus@pitlabs.in</a>
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">8. Policy Updates</h3>
                        <p>
                            Policies may be updated periodically.
                        </p>
                    </div>
                </div>
            </main>

            {/* Short Footer */}
            <footer className="bg-ayurmax-dark text-white py-8 text-center mt-auto">
                <p className="text-white/50">&copy; 2026 PIT Labs. All Rights Reserved.</p>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
